import Phaser from "phaser";
import { WalkChapter } from "./WalkChapter";
import { CENTRAL } from "../data/central";
import type { ChapterData } from "../data/content-types";
import { isoDepth, TILE_H } from "../core/iso";
import * as P from "../art/props";

/** CAPÍTULO 6 — "Tensión". Subestación eléctrica: fondo, suelo y decorado propios. */
export class CentralScene extends WalkChapter {
  protected chapter: ChapterData = CENTRAL;
  protected grid = { w: 15, h: 11 };
  protected spawn = { x: 7, y: 9 };
  protected worldSigns = [
    { id: "w_elec", x: 1.4, y: 0.3 },
    { id: "p_agua", x: 5.5, y: 0.3 },
    { id: "m_guantes", x: 9.5, y: 0.3 },
    { id: "m_botas", x: 13.5, y: 0.3 },
  ];
  private decor: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super("central");
  }

  protected floorKind(x: number, y: number): P.TileKind {
    const h = (x * 928371 + y * 12347) & 255;
    return h % 14 === 0 ? "dirt" : "concrete";
  }

  protected buildBackdrop(): void {
    const cam = this.cameras.main;
    cam.setBackgroundColor(0x585c62);
    const W = 1280, H = 720, over = 420;
    const sky = this.add.graphics().setScrollFactor(0).setDepth(-19000);
    sky.fillGradientStyle(0x767a80, 0x767a80, 0x585c62, 0x585c62, 1);
    sky.fillRect(-over, -over, W + over * 2, H * 0.6 + over);
    // torres de alta tensión (celosía) + cables
    const sk = this.add.graphics().setScrollFactor(0.25).setDepth(-18900);
    const base = H * 0.44;
    const pylon = (cx: number) => {
      sk.lineStyle(2, 0x4a4f57, 0.9);
      sk.beginPath(); sk.moveTo(cx - 16, base); sk.lineTo(cx, base - 155); sk.lineTo(cx + 16, base); sk.strokePath();
      sk.beginPath(); sk.moveTo(cx - 16, base); sk.lineTo(cx + 16, base - 44); sk.moveTo(cx + 16, base); sk.lineTo(cx - 16, base - 44); sk.strokePath();
      for (const y of [base - 92, base - 122]) { sk.beginPath(); sk.moveTo(cx - 36, y); sk.lineTo(cx + 36, y); sk.strokePath(); }
    };
    pylon(200); pylon(560); pylon(920);
    sk.lineStyle(1.5, 0x3a3f47, 0.6);
    for (const y of [base - 90, base - 120]) { sk.beginPath(); sk.moveTo(200, y); sk.lineTo(560, y + 8); sk.lineTo(920, y); sk.strokePath(); }
    // paredes de hormigón de la sala eléctrica
    this.buildWalls({ left: 0x545a61, right: 0x666d75, height: 168, base: 0x3d4147, texture: "tile" });
  }

  protected buildDecor(): void {
    this.decor.forEach((d) => d.destroy());
    this.decor = [];
    const put = (obj: Phaser.GameObjects.Container, x: number, y: number): void => {
      const p = this.iso(x, y);
      obj.setPosition(p.sx, p.sy).setDepth(isoDepth(x, y));
      this.decor.push(obj);
    };
    // franja de peligro eléctrica (amarillo/negro) por delante del cuadro
    const g = this.add.graphics().setDepth(-9500);
    g.lineStyle(4, 0xe6c200, 0.85);
    for (const yy of [5.0, 5.9]) { const a = this.iso(3, yy), b = this.iso(this.grid.w - 3, yy); g.beginPath(); g.moveTo(a.sx, a.sy + TILE_H / 2); g.lineTo(b.sx, b.sy + TILE_H / 2); g.strokePath(); }
    this.decor.push(g);
    put(P.transformer(this), 2.4, 2.6);
    put(P.transformer(this, 0x7d848a), 3.6, 3.2);
    put(P.machine(this, 0x4a545a), 12.4, 2.6);
    put(P.gascylinder(this, 0xb23a2a), 1.6, 5.4);
    put(P.gascylinder(this, 0xd8b93f), 2.1, 5.7);
    put(P.pipes(this), 12.6, 8.6);
    put(P.warningSign(this), 8.5, 4.4);
    put(P.extinguisher(this), 1.4, 3.2);
    put(P.cone(this), 6, 6.4);
    put(P.cone(this), 9, 5.2);
    put(P.fence(this), 4.6, 9.6);
    put(P.fence(this), 6.1, 9.7);

    // --- más equipo eléctrico (densidad) ---
    put(P.transformer(this, 0x6a727a), 13.6, 6.2);
    put(P.machine(this, 0x3a4048), 9.6, 2.2);
    put(P.gasrack(this), 13.6, 8.6);
    put(P.shelving(this), 1.6, 8.6);
    put(P.toolbox(this, 0x2f6b8f), 3.2, 6.6);
    put(P.hosecoil(this, 0x2a2f36), 5.2, 3.0); // rollo de cable
    put(P.hosecoil(this, 0x14141a), 10.6, 8.2);
    put(P.crateStack(this), 12.4, 4.2);

    // detalle de suelo: canaleta de cables + rejillas
    const gd = this.add.graphics().setDepth(-9400);
    { const a = this.iso(2, 3.2), b = this.iso(13, 3.2); gd.lineStyle(6, 0x3a3f47, 0.5); gd.beginPath(); gd.moveTo(a.sx, a.sy + TILE_H / 2); gd.lineTo(b.sx, b.sy + TILE_H / 2); gd.strokePath(); gd.lineStyle(2, 0x14141a, 0.4); gd.beginPath(); gd.moveTo(a.sx, a.sy + TILE_H / 2); gd.lineTo(b.sx, b.sy + TILE_H / 2); gd.strokePath(); }
    for (const [dx, dy] of [[7, 6.4], [11, 7]] as [number, number][]) { const p = this.iso(dx, dy), cy = p.sy + TILE_H / 2; gd.fillStyle(0x3a3f47, 0.5); gd.fillRect(p.sx - 11, cy - 4, 22, 9); gd.lineStyle(1, 0x14141a, 0.6); for (let i = -8; i <= 8; i += 3) { gd.beginPath(); gd.moveTo(p.sx + i, cy - 4); gd.lineTo(p.sx + i, cy + 5); gd.strokePath(); } }
    this.decor.push(gd);

    // luminarias industriales + focos
    for (const [lx, ly] of [[3.5, 3], [7.5, 3], [11.5, 3], [5.5, 6.5], [9.5, 6.5]] as [number, number][]) {
      const p = this.iso(lx, ly);
      const pg = this.add.graphics().setDepth(-9300); const pcy = p.sy + TILE_H / 2; pg.fillStyle(0xffe6a0, 0.14); pg.fillEllipse(p.sx, pcy, 80, 38); pg.fillStyle(0xfff4c8, 0.13); pg.fillEllipse(p.sx, pcy, 45, 22); this.decor.push(pg); this.addPulse(pg, 0.13, 0.005, 0.86);
      const lamp = P.hanglamp(this, 150); lamp.setPosition(p.sx, p.sy).setDepth(50000); this.decor.push(lamp); this.addSway(lamp);
    }

    // busbar de cobre + bandeja de cables en la pared trasera
    const wf = this.add.graphics().setDepth(-11900);
    const wa = this.iso(-0.5, -0.5), wb = this.iso(this.grid.w - 0.5, -0.5), wdl = this.iso(-0.5, this.grid.h - 0.5);
    wf.lineStyle(4, 0x8a5a10, 0.85); wf.beginPath(); wf.moveTo(wa.sx, wa.sy - 122); wf.lineTo(wb.sx, wb.sy - 122); wf.strokePath();
    wf.lineStyle(4, 0x3a3f47, 0.9); wf.beginPath(); wf.moveTo(wa.sx, wa.sy - 96); wf.lineTo(wb.sx, wb.sy - 96); wf.moveTo(wa.sx, wa.sy - 96); wf.lineTo(wdl.sx, wdl.sy - 96); wf.strokePath();
    this.decor.push(wf);

    // chispas del cable dañado (7,6) — riesgo eléctrico vivo
    this.addRisers(7, 6, { count: 6, color: 0xfff2a0, alpha: 0.95, r: 1.7, rise: 18, sp: 0.02, grow: 0.2, dyBase: 8, depth: 45000 });
    this.addRisers(7, 6, { count: 3, color: 0xffffff, alpha: 0.9, r: 1.1, rise: 22, sp: 0.028, grow: 0.1, dx: 4, dyBase: 6, depth: 45000 });
    // vapor de la fuga de agua (5,6) junto al cuadro en tensión
    this.addRisers(5, 6, { count: 3, color: 0xbcd0e0, alpha: 0.26, r: 4, rise: 30, sp: 0.005, grow: 1.6, dyBase: 8, depth: 30000 });

    if (this.state.chapter("central").done) this.onResolved();
  }

  protected onResolved(): void {
    const c = this.iso(7, 4);
    this.add.rectangle(c.sx, c.sy - 34, 16, 14, 0x2f8f57).setStrokeStyle(2, 0x1e6b3f).setDepth(70);
    this.add.text(c.sx, c.sy - 56, "NO CONECTAR", { fontSize: "10px", color: "#fff", backgroundColor: "#b23a2a", padding: { x: 4, y: 2 } }).setOrigin(0.5).setDepth(71);
  }
}
